import React from 'react';
import {useTranslation} from 'react-i18next';
import {compose} from 'redux';
import {createStructuredSelector} from 'reselect';
import {selectDevices} from '../../../redux/selectors/device.selector';
import {connect} from 'react-redux';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import {Comment, fontSize, Secondary, Text} from '../../../utils/style/texts';
import DialogActions from '@mui/material/DialogActions';
import {writeConfig} from '../../../containers/DevCon/actions';
import {useSnackbar} from 'notistack';
import {fromJS} from 'immutable';
import List from '@mui/material/List';
import * as styles from './style';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '../../Controls/Checkbox';
import Divider from '@mui/material/Divider';
import Chip from '../../Chip';
import {Flex} from '../../Container/Flex';
import SlideUpTransition from '../../SlideUpTransition';
import Dialog from '../../Dialog';
import TextButton from '../../Controls/TextButton';
import FormControlLabel from "@mui/material/FormControlLabel";
import {ClickLink} from "../../Link";
import Alert from "../../Alert";
import ListItemButton from "@mui/material/ListItemButton";

// Render a setting value for the diff (booleans ✓/✗, empties —, arrays of objects
// as a count, else stringified).
const formatSettingValue = (v, t) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? '✓' : '✗';
    if (Array.isArray(v)) {
        if (!v.length) return '—';
        if (v.some(x => x && typeof x === 'object')) return `${v.length} ${t('items', {defaultValue: 'items'})}`;
        return v.map(x => (x && typeof x === 'object') ? JSON.stringify(x) : String(x)).join(', ');
    }
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

const SelectDevicesForConfigDialog = props => {
    const [t] = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const [deviceIds, setDeviceIds] = React.useState(fromJS([]));
    const [onlyChanges, setOnlyChanges] = React.useState(true);

    const {diff, device, writeConfig, onClose, onSaveV4, isOpen, devices} = props;
    const saveOnly = !!diff?.saveOnly;

    React.useEffect(() => {
        setDeviceIds(fromJS([device.id]));
    }, [setDeviceIds, isOpen, device.id]);

    const onWrite = reboot => {
        if (onSaveV4) {
            // V4 toast is shown by the push-consolidation saga once it completes.
            onSaveV4(deviceIds, onlyChanges, reboot);
        } else {
            enqueueSnackbar(saveOnly ? t('saved') : t('writingConfigurationOneMoment'));
            writeConfig(device.id, reboot, deviceIds, onlyChanges);
            onClose();
        }
    }

    const toggleDevice = deviceId => {
        if (deviceIds.contains(deviceId)) {
            const index = deviceIds.indexOf(deviceId);
            setDeviceIds(deviceIds.remove(index));
        } else {
            setDeviceIds(deviceIds.push(deviceId));
        }
    }

    const renderDevice = device => {
        return (
            <ListItemButton key={device.id} onClick={() => toggleDevice(device.id)}>
                <Checkbox
                    checked={deviceIds.contains(device.id)}
                    onChange={() => toggleDevice(device.id)}
                />
                <ListItemText primary={
                    <Flex fullWidth>
                        <Text style={{flex: 1}}>{device.get('name')}</Text>
                        <Chip
                            small={1}
                            className="size-9-deep"
                            color={device.color}
                            label={t(device.statusName)}/>
                    </Flex>
                }/>
            </ListItemButton>
        );
    }

    const shouldReboot = !!deviceIds.find(dId => !!devices.find(d => d.id === dId && !d.isApp && !d.isEmerit));
    const otherDevices = devices.filter(d => d.id !== device.id && device.isSameTypeAs(d));
    const allSelected = !!otherDevices?.find(d => !!deviceIds?.find(nd => nd === d.id));

    return (
        <Dialog
            open={!!isOpen}
            onError={{
                catch: reset => {
                    onClose();
                    reset();
                }
            }}
            TransitionComponent={SlideUpTransition}
            fullWidth
            maxWidth="sm"
            onClose={() => onClose()}>
            <DialogTitle>{saveOnly ? t('saveConfig2') : t('writeConfigToDevice')}</DialogTitle>
            <DialogContent style={{minWidth: 300}}>
                {diff?.diff && !diff.diff.hasChanges && (
                    <Alert type="info" dismissable={false}>{t('noChanges.devices', {device: device.name})}</Alert>
                )}
                {Array.isArray(diff?.settingsChanges) && (
                    diff.settingsChanges.length > 0 ? (
                        <styles.DiffList>
                            <Secondary style={{display: 'block', marginBottom: 6}}>
                                {t('pendingChanges', {defaultValue: 'Settings to be saved'})}
                            </Secondary>
                            {diff.settingsChanges.map(c => {
                                const from = formatSettingValue(c.from, t);
                                const to = formatSettingValue(c.to, t);
                                return (
                                    <Flex key={c.id} align="center" className="diff-row" fullWidth>
                                        <Text style={{flex: 1}}>{c.label || t(c.name)}</Text>
                                        <Comment className="diff-from" title={from}>{from}</Comment>
                                        <span className="diff-arrow">→</span>
                                        <Text className="diff-to" title={to}>{to}</Text>
                                    </Flex>
                                );
                            })}
                        </styles.DiffList>
                    ) : (
                        <Alert type="info" dismissable={false}>
                            {t('noChangesToSave', {defaultValue: 'No changes to save'})}
                        </Alert>
                    )
                )}
                {otherDevices.size > 0 && (
                    <styles.ListWrapper>
                        <List>
                            {devices.filter(d => d.id === device.id).map(d => renderDevice(d))}
                            <Divider/>
                        </List>
                        {saveOnly ? (
                            <Secondary>{t('alsoSaveToOtherDevices', {type: device.deviceTypeName(t)})}</Secondary>    
                        ) : (
                            <Secondary>{t('alsoWriteToOtherDevices', {type: device.deviceTypeName(t)})}</Secondary>
                        )}
                        <ClickLink
                            onClick={() => setDeviceIds(otherDevices.push(device).filter(d => !allSelected || d.id === device.id).map(d => d.id))}
                            style={{
                                marginLeft: 10,
                                fontSize: fontSize.m
                            }}>{allSelected ? t('deselectAll') : t('selectAll')}</ClickLink>
                        <styles.Scroll>
                            <List>
                                {otherDevices.map(d => renderDevice(d))}
                            </List>
                        </styles.Scroll>
                    </styles.ListWrapper>
                )}

                {shouldReboot && !saveOnly && (
                    <DialogContentText>
                        <Text>{t('rebootAfterConfig.confirm')}</Text>
                    </DialogContentText>
                )}
                {!onSaveV4 && !!deviceIds.filter(i => device.id !== i).size && (
                    <DialogContentText>
                        <FormControlLabel
                            style={{marginLeft: 0, marginTop: 32}}
                            value={onlyChanges}
                            disabled={saveOnly}
                            control={(
                                <Checkbox
                                    checked={onlyChanges}
                                    onChange={() => setOnlyChanges(!onlyChanges)}
                                />
                            )}
                            label={<Flex
                                col><Text>{t('pushOnlyChanges?')}</Text><Comment>{t('pushEverything.explanation')}</Comment></Flex>}
                            labelPlacement="end"
                        />
                    </DialogContentText>
                )}
            </DialogContent>
            <DialogActions>
                <TextButton onClick={() => onClose()}>
                    {t('cancel')}
                </TextButton>
                {saveOnly ? (
                    <>
                        <TextButton color="primary" onClick={() => onWrite(false)}>
                            {t('save')}
                        </TextButton>
                        {/* V4 only; hidden for software apps that can't reboot. */}
                        {onSaveV4 && !device.isApp && !device.isCueblyMobile && (
                            <TextButton color="primary" onClick={() => onWrite(true)}>
                                {t('saveAndReboot', {defaultValue: 'Save + reboot'})}
                            </TextButton>
                        )}
                    </>
                ) : (
                    <>
                        {shouldReboot && (
                            <TextButton onClick={() => onWrite(false)}>
                                {t('noJustConfigure')}
                            </TextButton>
                        )}
                        <TextButton onClick={() => onWrite(true)} color="primary">
                            {shouldReboot ? t('yesRebootDirectly') : t('configure')}
                        </TextButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

function mapDispatchToProps(dispatch) {
    return {
        writeConfig: (deviceId, reboot, deviceIds, onlyChanges) => dispatch(writeConfig(deviceId, reboot, deviceIds, onlyChanges)),
    };
}

const mapStateToProps = createStructuredSelector({
    devices: selectDevices()
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps)
)(SelectDevicesForConfigDialog);